import { Link } from 'react-router-dom';
import type { MealPlanEntry } from '../../../shared/types';
import type { Resource } from '../useResource';
import { dinnerFor } from '../homeData';
import { Card, CardTitle, Empty, ErrorState, Icon, LoadingRows } from '../ui';

/**
 * Tonight's dinner. Shared by both views — it's the question the family actually
 * asks at 5pm.
 *
 * Photo vs. gradient: a real photo when the meal has one in R2 (meal.photo_url),
 * a warm gradient when it doesn't. No stock imagery — the only photos in this app
 * are ones the family took.
 */
export default function DinnerCard({
  res,
  date,
}: {
  res: Resource<MealPlanEntry[]>;
  date: string;
}) {
  const { data, loading, error, reload } = res;
  const entry = dinnerFor(data, date);
  const meal = entry?.meal ?? null;

  return (
    <Card className="overflow-hidden">
      {!loading && !error && meal && (
        <div className="relative h-36 w-full sm:h-44">
          {meal.photo_url ? (
            <img
              src={meal.photo_url}
              alt={meal.name}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="grid h-full w-full place-items-center bg-gradient-to-br from-honey-soft via-clay-soft to-sage-soft text-honey">
              <Icon name="pot" className="h-10 w-10" />
            </div>
          )}
        </div>
      )}

      <div className="p-5">
        <CardTitle icon="pot" action={{ to: '/meals', label: 'Meals' }}>
          Tonight
        </CardTitle>

        <div className="mt-4">
          {loading && <LoadingRows rows={2} />}
          {!loading && error && <ErrorState what="tonight's dinner" onRetry={reload} />}

          {!loading && !error && !meal && (
            <Empty
              message="No dinner planned for tonight. Pick something and it lands on everyone's home screen."
              action={{ to: '/meals', label: "Plan tonight's dinner" }}
            />
          )}

          {!loading && !error && meal && (
            <>
              <p className="font-display text-2xl leading-tight font-semibold text-ink">
                {meal.name}
              </p>
              {meal.notes && <p className="mt-1.5 text-sm text-ink-soft">{meal.notes}</p>}
              <Link
                to="/meals"
                className="mt-2 inline-flex min-h-11 items-center gap-1.5 text-sm font-medium text-sage hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
              >
                See this week&rsquo;s plan
                <Icon name="arrow" className="h-4 w-4" />
              </Link>
            </>
          )}
        </div>
      </div>
    </Card>
  );
}
